import { CUSTOMER_RETURN_CONFIG } from '@/config/customerReturn.config'

/**
 * Customer Return Interface Detection Composable
 *
 * Detects and categorizes customer interface types during the return flow,
 * particularly identifying custom interfaces that require special handling.
 */

/**
 * @typedef {Object} CustomerInterface
 * @property {string} [type] - Primary interface type
 * @property {Array<{type: string}>} [inputs] - Array of input configurations
 */

/**
 * Composable for detecting customer interface types
 * @returns {Object} Interface detection functions
 */
export function useCustomerReturnInterfaceDetection() {
  /**
   * Check if an interface type requires custom handling
   * @param {string} type - Interface type to check
   * @returns {boolean} True if type is custom
   */
  function isCustomType(type) {
    if (!type) return false
    return CUSTOMER_RETURN_CONFIG.interface.CUSTOM_TYPES.includes(type)
  }

  /**
   * Determine if a customer interface requires custom handling
   * A custom interface is one that does not utilize the standard page title
   * and message from the LLM, but instead has its own specialized UI.
   *
   * @param {CustomerInterface|null} customerInterface - Interface configuration from AI
   * @returns {boolean} True if interface requires custom handling
   */
  function isCustomInterface(customerInterface) {
    if (!customerInterface) return false

    // Check if primary interface type is custom
    if (customerInterface.type && isCustomType(customerInterface.type)) {
      return true
    }

    // Check if any nested inputs are custom types
    // Note: This will be problematic if LLM returns mixed generic + custom interfaces
    const inputs = customerInterface.inputs || []
    return inputs.some(input => input.type && isCustomType(input.type))
  }

  /**
   * Check if interface requires photo upload
   * @param {CustomerInterface|null} customerInterface - Interface configuration
   * @returns {boolean} True if photo upload is required
   */
  function requiresPhotoUpload(customerInterface) {
    if (!customerInterface) return false

    // Check primary interface type
    if (customerInterface.type === 'uploadPhotos') {
      return true
    }

    // Check nested inputs
    const inputs = customerInterface.inputs || []
    return inputs.some(input => input.type === 'uploadPhotos')
  }

  /**
   * Get the primary interface type
   * @param {CustomerInterface|null} customerInterface - Interface configuration
   * @returns {string|null} Primary interface type or null
   */
  function getInterfaceType(customerInterface) {
    return customerInterface?.type || null
  }

  /**
   * Get all input types from interface configuration
   * @param {CustomerInterface|null} customerInterface - Interface configuration
   * @returns {string[]} Array of input types
   */
  function getInputTypes(customerInterface) {
    if (!customerInterface?.inputs || !Array.isArray(customerInterface.inputs)) {
      return []
    }

    return customerInterface.inputs.map(input => input.type).filter(type => type != null)
  }

  /**
   * Check if interface has a specific input type
   * @param {CustomerInterface|null} customerInterface - Interface configuration
   * @param {string} inputType - Type to look for
   * @returns {boolean} True if input type exists
   */
  function hasInputType(customerInterface, inputType) {
    if (!customerInterface || !inputType) return false

    // Check primary type
    if (customerInterface.type === inputType) {
      return true
    }

    // Check inputs array
    const inputTypes = getInputTypes(customerInterface)
    return inputTypes.includes(inputType)
  }

  /**
   * Determine if interface requires user input
   * @param {CustomerInterface|null} customerInterface - Interface configuration
   * @returns {boolean} True if user input is needed
   */
  function requiresUserInput(customerInterface) {
    if (!customerInterface) return false

    // Interface with no type or inputs likely doesn't need input
    if (!customerInterface.type && (!customerInterface.inputs || customerInterface.inputs.length === 0)) {
      return false
    }

    return true
  }

  return {
    getInputTypes,
    getInterfaceType,
    hasInputType,
    isCustomInterface,
    isCustomType,
    requiresPhotoUpload,
    requiresUserInput,
  }
}
